import { APP_COUNTRY } from 'config';
import {
  BaseApiErrorResponse,
  baseApiResponseSchema,
} from 'services/api/api-config';
import { makeSelectAuthApiConfig } from 'services/api/api.selectors';
import { RequestApiDataIfNecessaryPayload } from 'services/api/api.types';
import { AppState, ReduxSelector } from 'store/types';
import { useQuery } from 'utils/hooks/query/use-query';
import { z } from 'zod/v4';

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

type UserPersonalDataServiceErrorResponse = BaseApiErrorResponse;

export const userPersonalDataTypeResp = baseApiResponseSchema.extend({
  responseObject: z
    .object({
      account: z
        .object({
          code: z.string(),
          state: z.enum(['ACTIVE', 'INACTIVE', 'SUSPENDED']).or(z.string()),
          type: z.enum(['ONLINE', 'OFFLINE']).or(z.string()),
          createdTimestamp: z.number(),
          createdBy: z.string(),
          createdClientId: z.number(),
          lastModifiedTimestamp: z.number(),
          lastModifiedClientId: z.number(),
          lastModifiedBy: z.string(),
          accountInfo: z.object({
            channelId: z.number(),
            promoExt: z.string().nullable(),
            userAgent: z.string(),
            openingTypeCode: z.string(),
            affiliationData: z.string().nullable(),
          }),
          agreement: z.object({
            code: z.string(),
            channelId: z.number(),
            sent: z.boolean(),
            createdTimestamp: z.number(),
            createdBy: z.string(),
            createdIp: z.string(),
            createdClientId: z.number(),
            lastModifiedTimestamp: z.number(),
            lastModifiedBy: z.string(),
            lastModifiedIp: z.string(),
            lastModifiedClientId: z.number(),
          }),
        })
        .optional(),

      user: z
        .object({
          firstName: z.string(),
          lastName: z.string(),
          gender: z.enum(['M', 'F', 'O']).or(z.string()),
          birthDate: z.string(), // ISO date string
          nationalId: z.string(),
          nationalIdType: z.string(),
          username: z.string(),
          birthCity: z.string().optional(),
          birthCountry: z.string().optional(),
          birthDistrictCode: z.string().optional(),
          userContact: z.object({
            createdTimestamp: z.number(),
            createdIp: z.string(),
            createdBy: z.string(),
            createdClientId: z.number(),
            lastModifiedTimestamp: z.number(),
            lastModifiedBy: z.string(),
            lastModifiedIp: z.string(),
            lastModifiedClientId: z.number(),
            mobileArea: z.string(),
            mobile: z.string(),
            email: z.string().email(),
            mobileVerified: z.boolean(),
            emailVerified: z.boolean(),
            emailVerifiedTimestamp: z.number().nullable(),
          }),

          userDocument: z.object({
            createdTimestamp: z.number(),
            createdIp: z.string(),
            createdBy: z.string(),
            createdClientId: z.number(),
            lastModifiedTimestamp: z.number(),
            lastModifiedBy: z.string(),
            lastModifiedIp: z.string(),
            lastModifiedClientId: z.number(),
            type: z.string(),
            number: z.string(),
            issuingDate: z.string(),
            issuingPlace: z.string(),
            issuingAuthority: z.string(),
            expirationDate: z.string(),
          }),

          userPrivacyList: z.array(
            z.object({
              createdTimestamp: z.number(),
              createdIp: z.string(),
              createdBy: z.string(),
              createdClientId: z.number(),
              lastModifiedTimestamp: z.number(),
              lastModifiedBy: z.string(),
              lastModifiedIp: z.string(),
              lastModifiedClientId: z.number(),
              type: z
                .enum(['PRIVACY', 'EMAIL', 'PROFILE', 'SMS', 'CALL'])
                .or(z.string()),
              value: z.boolean(),
            }),
          ),

          userAddressList: z.array(
            z.object({
              createdTimestamp: z.number(),
              createdIp: z.string(),
              createdBy: z.string(),
              createdClientId: z.number(),
              lastModifiedTimestamp: z.number(),
              lastModifiedBy: z.string(),
              lastModifiedIp: z.string(),
              lastModifiedClientId: z.number(),
              city: z.string(),
              address: z.string(),
              country: z.string(),
              zipCode: z.string(),
              districtCode: z.string(),
              district: z.string(),
              type: z.enum(['RESIDENCE', 'DOMICILE']).or(z.string()),
            }),
          ),
        })
        .optional(),
    })
    .nullable(),
});

type UserPersonalDataIResponse = z.infer<typeof userPersonalDataTypeResp>;

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/

const userPersonalDataEndpoint = '/account-ms/v1/profile/personal-data';

export function makeUserPersonalDataApiConfig(): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function userPersonalDataApiConfig(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    return {
      resourceId: userPersonalDataEndpoint,
      persistResponseDataInsideApiReducer: true,
      successSchemaValidator: userPersonalDataTypeResp,
      requestConfig: makeSelectAuthApiConfig({
        url: userPersonalDataEndpoint,
        method: 'GET',
        enrichRequestData: false,
      })(reduxState),
    };
  };
}

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/

const useUserPersonalDataQuery = (isActive = APP_COUNTRY !== 'it') => {
  const { data, ...rest } = useQuery<
    UserPersonalDataIResponse,
    UserPersonalDataServiceErrorResponse
  >(userPersonalDataEndpoint, {
    apiConfig: makeUserPersonalDataApiConfig(),
    isActive,
  });

  return { data, ...rest };
};

export { userPersonalDataEndpoint, useUserPersonalDataQuery };
export type { UserPersonalDataIResponse };
